<template>
  <div
    id="multi-artwork-modal"
    ref="modal"
    class="modal multi-artwork-modal"
    data-testid="transfers-multi-artwork-modal"
    role="dialog"
    :class="{ 'multi-artwork-modal--bellow': moveBellow }"
  >
    <div class="multi-artwork-modal__modal-box modal-dialog">
      <div
        class="multi-artwork-modal__modal-header"
        role="heading"
        aria-label="Your files"
        tabIndex="0"
      >
        <h3 class="multi-artwork-modal__modal-title">
          <template v-if="pendingFiles.length">
            <Spinner size="small" class="multi-artwork-modal__top-icon" />
            Uploading {{ uploadedCount }} of {{ artworks.length }} files
          </template>
          <template v-else>
            <div>
              <UseSvg icon="check-circle" class="multi-artwork-modal__top-icon" />
            </div>
            {{ filesUploadedMessage }}
          </template>
        </h3>

        <button
          class="multi-artwork-modal__modal-button-close"
          type="button"
          data-testid="transfers-modal-close-button"
          @click="onClose"
        >
          <UseSvg icon="modal-close-big" />
        </button>
      </div>
      <div class="multi-artwork-modal__modal-body">
        <div class="multi-artwork-modal__artworks">
          <Artwork
            v-for="(item, index) in artworks"
            :key="index + String(item.id)"
            :default-active-config-section="index === 0 ? activeConfigSection : undefined"
            :artwork="item"
            :index="index"
            :is-loading="item.isLoading"
            :current-store="currentStore"
            :is-multi-upload="true"
            :show-free-transfers-badge="showFreeTransfersBadge"
            show-full-screen-button
          />

          <UploadBox v-if="!files.length" @upload="onUpload" />
        </div>
        <div class="multi-artwork-modal__summary" data-testid="transfers-modal-summary">
          <div class="multi-artwork-modal__delivery-date">
            <DeliveryDate :is-same-day="isSameDay" />
          </div>
          <slot name="free-shipping"></slot>

          <PreCut v-if="withPreCut" is-modal />
          <DesignServicesSubscribe />
          <Summary />
          <TransfersStoreCredit v-if="showStoreCredit" :store-credit-info="storeCreditInfo" />
          <slot name="saved-carts"></slot>
          <DtfAddToCart />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from "vue";
import { mapState, mapActions } from "pinia";
import initProductSavedCartsSelector from "components/jiffy/product-saved-carts-selector/product-saved-carts-selector";
import UseSvg from "common/vue/components/UseSvg.vue";
import TransfersStoreCredit from "./common/TransfersStoreCredit.vue";
import Spinner from "../vue-cart/Spinner.vue";
import Artwork from "./Artwork.vue";
import Summary from "./Summary.vue";
import UploadBox from "./UploadBox.vue";
import DtfAddToCart from "./DtfAddToCart.vue";
import DeliveryDate from "./DeliveryDate.vue";
import PreCut from "./PreCut.vue";
import DesignServicesSubscribe from "./ArtworkQualityConfigPanel/DesignServices/DesignServicesSubscribe.vue";
import { useDtfStore } from "./store";

export default {
  name: "MultiArtworkModal",
  // eslint-disable-next-line vue/no-reserved-component-names
  components: {
    UseSvg,
    Artwork,
    UploadBox,
    Spinner,
    // eslint-disable-next-line vue/no-reserved-component-names
    Summary,
    DtfAddToCart,
    PreCut,
    DesignServicesSubscribe,
    TransfersStoreCredit,
    DeliveryDate
  },
  props: {
    moveBellow: {
      type: Boolean,
      default: false
    },
    currentStore: {
      type: String,
      default: null
    },
    activeConfigSection: {
      type: String,
      default: "jiffy-print-perfect"
    }
  },
  emits: ["upload"],
  data() {
    return {
      preventClose: true,
      isSameDay: false,
      deliveryDateObserver: null
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "showModal",
      "artworks",
      "files",
      "loadedArtworks",
      "withPreCut",
      "isSticker",
      "isGuest",
      "storeCreditInfo",
      "blankItemsCount",
      "isBogoApplicable",
      "isNewToTransfers"
    ]),
    uploadedCount() {
      return this.loadedArtworks.length + 1;
    },
    pendingFiles() {
      return this.files.filter(({ uploadError }) => !uploadError);
    },
    filesUploadedMessage() {
      if (this.pendingFiles.length === this.files.length) {
        return "All files successfully uploaded";
      }

      return "Files have been uploaded";
    },
    showStoreCredit() {
      return !this.isSticker && !this.isGuest && Number(this.storeCreditInfo.orderSubtotal) > 0;
    },
    showFreeTransfersBadge() {
      if (!this.isBogoApplicable) {
        return false;
      }

      const allArtworksReady = this.artworks.every(
        artwork => !artwork.isLoading && !artwork.isFile
      );
      if (!allArtworksReady) {
        return false;
      }

      if (this.blankItemsCount <= 0) {
        return false;
      }

      if (this.isGuest) {
        return true;
      }

      if (this.isNewToTransfers === null || this.isNewToTransfers === undefined) {
        return false;
      }

      return this.isNewToTransfers === true;
    }
  },
  mounted() {
    this.onShow();
    // Watch for delivery date changes in the DOM But, just for scoped items to the multi-artwork modal
    this.setupDeliveryDateObserver();
    nextTick(() => {
      window.JiffyDeliverTo.reloadToggle();
    });
    initProductSavedCartsSelector();
    this.updatePdpDates();
  },
  beforeUnmount() {
    this.deliveryDateObserver?.disconnect();
    this.preventClose = false;
    $(this.$refs.modal).modal("hide");
  },
  methods: {
    $getModal() {
      return this.$refs.modal;
    },
    $getDeliveryDateTarget() {
      const $modal = this.$getModal();
      return $modal.querySelector(".js-product-details-delivery-dates-simple-direct-value");
    },
    getDeliveryDateText() {
      const $target = this.$getDeliveryDateTarget();
      return $target ? $target.textContent.trim().toLowerCase() : "";
    },
    setIsSameDay(isSameDay) {
      this.isSameDay = isSameDay;
    },
    updateIsSameDay() {
      const dateText = this.getDeliveryDateText();
      const isSameDay = dateText.includes("today");
      this.setIsSameDay(isSameDay);
    },
    setupDeliveryDateObserver() {
      const $target = this.$getDeliveryDateTarget();
      if ($target) {
        this.updateIsSameDay();
        this.deliveryDateObserver = new MutationObserver(() => this.updateIsSameDay());
        this.deliveryDateObserver.observe($target, { childList: true, subtree: true });
      }
    },
    ...mapActions(useDtfStore, ["setShowDiscardModal"]),
    ...mapActions(useDtfStore, ["updatePdpDates"]),
    onShow() {
      const $modal = $(this.$refs.modal);
      $modal
        .modal("show")
        .on("hide.bs.modal", event => {
          if (this.showModal && this.preventClose && event.target.id === $modal[0].id) {
            event.preventDefault();
            this.onClose();
          }
        })
        .on("hidden.bs.modal", () => {
          $modal.off("hidden.bs.modal");
          document.body.classList.remove("dtf-modal-open");
        });

      document.body.classList.add("dtf-modal-open");
    },
    onClose() {
      this.setShowDiscardModal();
    },
    onUpload() {
      this.$emit("upload");
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/modal.scss";

.multi-artwork-modal {
  @include app-modal;

  overflow-x: hidden;
  overflow-y: auto;

  &.modal {
    z-index: 9999;
  }

  &--bellow {
    &.modal {
      z-index: 1000;
    }
  }

  &__modal-box {
    top: 10%;
    max-height: unset;

    @include with-desktop {
      max-width: 970px;
      top: 0;
      overflow: hidden;
      height: 100%;
      max-height: calc(100% - 20px);
      margin: 10px auto;
      padding-bottom: 100px;
    }

    @include with-large-desktop {
      max-width: 1260px;
    }

    &.modal-dialog {
      transform: none !important;
    }
  }

  &__modal-body {
    display: flex;
    width: 100%;
    flex-direction: column;
    padding-bottom: 32px;
    flex: 1 1 0;

    @include with-desktop {
      flex-direction: row;
      max-height: 100%;
    }
  }

  &__modal-header {
    @include with-desktop {
      padding: 24px 32px;
    }
  }

  &__modal-button-close {
    top: -5px;
    right: -5px;

    @include with-desktop {
      top: 0;
      right: 10px;
    }
  }

  &__modal-title {
    display: flex;
    align-items: center;
    margin: 0 auto;
  }

  &__top-icon {
    width: 24px;
    height: 24px;
    margin-right: 12px;
    color: $feedback-success-darker;
  }

  &__artworks {
    flex: 1;
    min-width: 0;

    @include with-desktop {
      margin-right: 18px;
      padding-right: 24px;
      border-right: 1px solid $gray-border;
    }
  }

  &__summary {
    width: 100%;

    @include with-desktop {
      flex: 0 0 382px;
      padding-right: 32px;
      position: sticky;
      top: 0;
      max-height: 100vh;
      overflow: auto;
    }
  }

  &__delivery-date {
    margin-bottom: 16px;
  }
}
</style>

<style lang="scss">
@import "frontend/init/jiffy/mixin.scss";

.dtf-modal-open {
  overflow: hidden;
}

.multi-artwork-modal {
  .product-details-delivery-date-simple {
    @include with-desktop {
      margin-top: 0;
    }

    &__wrapper {
      padding: 25px 12px 50px 25px;
    }

    &__delivery {
      align-items: center;
      justify-content: center;
      gap: 5px;
    }

    &__delivery-icon {
      position: unset;
    }

    &__cutoff {
      text-align: center;
    }
  }

  .design-services__subscribe {
    margin: 16px 0;
    padding: 8px;
    border-radius: 8px;
    background: #f8fafc;
  }
}
</style>
